import { ReactElement, useMemo, useCallback } from 'react';
import { Collapse, Stack } from '@mui/material';
import { SelectRowProps, TableChangeHandler } from 'react-bootstrap-table-next';
import { useSnackbar } from 'notistack';

import {
  DataTable,
  CustomNoRecordsIndicator,
  DialogConfimation,
  DialogCancelPartnerItem,
  GeoError,
  DialogDetailsOrder,
  DialogChangeOrderState,
  DialogModifyAssignmentOrders,
} from '~components/index';
import {
  useAppSelector,
  useLazyRequest,
  useExportTickets,
  useExportTicketsMl,
} from '~hooks/index';
import {
  useDataTableSelectedProps,
  SelectRowsHandlerOnSelectAll,
  SelectRowsHandlerOnSelect,
} from '~hooks/useDataTableSelectedProps';
import { selectAuth } from '~store/slices/auth/index';
import { deleteItems } from '~services/item';
import { ItemExtended } from '~services/item/types';
import { NotificationRefreshItem } from '~globals/types/notifications';
import { isOnlyReadUser } from '~utils/user';
import NoRecordsFoundMessage from '~app/components/DataTable/NoRecordsFoundMessage';
import { useSignalRContext } from '~contexts/SignalRContext';
import { useClientOnMethod } from '~hooks/useSignalR';
import { METHODS_NAME } from '~constants/signalR';

import { useDashboardVariantV2CarrierContext } from '../../../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierInWarehouseFiltersContext } from '../../../../../InWarehouseFiltersContext';
import { useDashboardVariantV2CarrierContentInWarehouseTabContext } from '../../Context';

import { ITEM_KEY_ID } from '../../utils';
import { DashboardVariantV2CarrierContentDataTableContainer } from '../../../../styles';

import { useInWarehouseTabDataTableColumns } from './useColumns';
import { CUSTOM_SORT_FIELDS } from './customSortFields';

const DashboardVariantV2CarrierContentInWarehouseTabDataTable =
  (): ReactElement => {
    const { enqueueSnackbar } = useSnackbar();
    const { hubConnection } = useSignalRContext();

    const { user } = useAppSelector(selectAuth);

    const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);

    const {
      ignoreRefreshBySocket: {
        value: ignoreRefreshBySocket,
        toggleValue: toggleIgnoreRefreshBySocket,
      },
      expandedHasChangeOrdersAlert: {
        toggleValue: toggleExpandedHasChangeOrdersAlert,
      },
      modifiedItemsIdBySocket: { setValue: setModifiedItemsIdBySocket },
      dialogs: {
        importFromFile: { toggleOpen: toggleOpenDialogImportFromFile },
        importFromIntegration: {
          toggleOpen: toggleOpenDialogImportFromIntegration,
        },
        updateGeoError: {
          open: openDialogUpdateGeoError,
          toggleOpen: toogleOpenDialogUpdateGeoError,
        },
      },
    } = useDashboardVariantV2CarrierContext();

    const {
      filters: { isFilterApplied },
    } = useDashboardVariantV2CarrierInWarehouseFiltersContext();

    const {
      parseFiltersRequest,
      selection: { selectedIds, utils: selectedIdsUtils },
      itemsWithGeoErrors,
      table: {
        data,
        loading: loadingRequestTable,
        totalSize,
        queryParams: { pagination },
        handleChangeTable: changeTable,
        showTable: { open: showTable },
      },
      map: {
        showMap: { open: showMap },
      },
      actions: { reloadDataAndResetSelectedRows },
      dialogs: {
        confirmDeleteOrder: {
          open: openConfirmDeleteOrder,
          data: selectedOrderDelete,
          handleClose: handleCloseConfirmDeleteOrder,
        },
        cancelOrder: {
          open: openDialogCancelOrder,
          data: selectedCancelOrder,
          handleClose: handleCloseDialogCancelOrder,
        },
        detailsOrder: {
          open: openDialogDetailsOrder,
          data: selectedDataForDetailsOrder,
          handleClose: handleCloseDialogDetailsOrder,
        },
        changeStateOrder: {
          open: openConfirmChangeStateOrder,
          data: selectedOrderDataToChangeState,
          handleClose: handleCloseDialogChangeStateOrder,
        },
        modifyAssignmentOrder: {
          open: openDialogModifyAssignmentOrder,
          data: selectedItemIdToModifyAssignment,
          handleClose: handleCloseDialogModifyAssignmentOrder,
        },
      },
    } = useDashboardVariantV2CarrierContentInWarehouseTabContext();

    const { loading: loadingExportTicket, onExportTickets: onExporTicket } =
      useExportTickets();

    const handleExportTicket = useCallback(
      async (trackingCode: string) => {
        await onExporTicket({ trackingCodes: [trackingCode] });
      },
      [onExporTicket],
    );

    const { loading: loadingExportTicketMl, onExportTicketsMl } =
      useExportTicketsMl();

    const handleExportTicketMl = useCallback(
      async (id: string) => {
        await onExportTicketsMl([id]);
      },
      [onExportTicketsMl],
    );

    const columns = useInWarehouseTabDataTableColumns({
      showMap,
      loadingExportTicket,
      onExporTicket: handleExportTicket,
      loadingExportTicketMl,
      onExportTicketMl: handleExportTicketMl,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChangeTable = useCallback<TableChangeHandler<any>>(
      (type, newState) => {
        toggleExpandedHasChangeOrdersAlert(false);
        toggleIgnoreRefreshBySocket(false);
        setModifiedItemsIdBySocket([]);

        const sortField =
          CUSTOM_SORT_FIELDS[newState?.sortField] ?? newState?.sortField;

        changeTable(type, {
          pagination: { ...newState, sortField },
          filters: parseFiltersRequest(),
        });
      },
      [
        changeTable,
        toggleExpandedHasChangeOrdersAlert,
        toggleIgnoreRefreshBySocket,
        setModifiedItemsIdBySocket,
        parseFiltersRequest,
      ],
    );

    const handleOnSelectAll = useCallback<
      SelectRowsHandlerOnSelectAll<ItemExtended>
    >(
      (isSelected, rows) => {
        if (isSelected) {
          const ids = rows.map((row) => row[ITEM_KEY_ID]);
          selectedIdsUtils.addMultiple(ids);
        } else {
          selectedIdsUtils.reset();
        }
      },
      [selectedIdsUtils],
    );

    const handleOnSelect = useCallback<SelectRowsHandlerOnSelect<ItemExtended>>(
      (row) => {
        const currentId = row[ITEM_KEY_ID];
        selectedIdsUtils.toggle(currentId);
      },
      [selectedIdsUtils],
    );

    const selectRowsProps = useDataTableSelectedProps({
      onSelectAll: handleOnSelectAll,
      onSelect: handleOnSelect,
      selectedIds,
    });

    const dataTableSelectRowProps = useMemo<
      SelectRowProps<ItemExtended> | undefined
    >(
      () => (isUserOnlyReader ? undefined : selectRowsProps),
      [isUserOnlyReader, selectRowsProps],
    );

    const getNoRecordsIndicator = useCallback(() => {
      if (isFilterApplied) return <NoRecordsFoundMessage />;

      return (
        <CustomNoRecordsIndicator
          indicatorType="orders"
          handleImportFile={() => {
            toggleOpenDialogImportFromFile(true);
          }}
          handleImportInt={() => {
            toggleOpenDialogImportFromIntegration(true);
          }}
        />
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFilterApplied]);

    const messageDialogConfimationDeleteOrder = useMemo(
      () => `El pedido ${selectedOrderDelete?.trackingCode} será eliminado`,
      [selectedOrderDelete],
    );

    const [, loadingDeleteOrder, , executeDeleteOrder] = useLazyRequest({
      request: deleteItems,
      withPostSuccess: () => {
        const trackingCode = selectedOrderDelete?.trackingCode;
        const message = `El pedido ${trackingCode} fue eliminado correctamente`;

        toggleIgnoreRefreshBySocket(true);

        enqueueSnackbar(message, { variant: 'success' });

        handleCloseConfirmDeleteOrder();

        reloadDataAndResetSelectedRows();
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo elimnar el pedido, intente nuevamente', {
          variant: 'error',
        });
      },
    });

    const handleDeleteOrder = useCallback(() => {
      if (selectedOrderDelete)
        executeDeleteOrder([selectedOrderDelete[ITEM_KEY_ID]]);
    }, [executeDeleteOrder, selectedOrderDelete]);

    const handleSuccessChangeOrdersState = useCallback(() => {
      toggleIgnoreRefreshBySocket(true);

      handleCloseDialogChangeStateOrder();

      reloadDataAndResetSelectedRows();
    }, [
      reloadDataAndResetSelectedRows,
      toggleIgnoreRefreshBySocket,
      handleCloseDialogChangeStateOrder,
    ]);

    const updateItem = useCallback(
      (notification) => {
        const infoNotification = JSON.parse(
          notification,
        ) as NotificationRefreshItem;

        if (!ignoreRefreshBySocket) {
          setModifiedItemsIdBySocket(infoNotification.ItemIds);
          toggleExpandedHasChangeOrdersAlert(true);
        }

        setTimeout(() => {
          toggleIgnoreRefreshBySocket(false);
        }, 1000);
      },
      [
        ignoreRefreshBySocket,
        toggleExpandedHasChangeOrdersAlert,
        toggleIgnoreRefreshBySocket,
        setModifiedItemsIdBySocket,
      ],
    );

    useClientOnMethod(hubConnection, METHODS_NAME.REFRESH_ITEM, updateItem);

    return (
      <>
        <Collapse orientation="horizontal" in={showTable}>
          <Stack height="100%">
            {!isUserOnlyReader && (
              <GeoError.Alert
                type="item"
                errorsCount={itemsWithGeoErrors}
                onEdit={() => toogleOpenDialogUpdateGeoError(true)}
                sx={(currentTheme) => ({
                  padding: currentTheme.spacing(0, 2, 2, 2),
                })}
              />
            )}

            <DashboardVariantV2CarrierContentDataTableContainer
              fullWidth={!showMap}
            >
              <DataTable
                loading={loadingRequestTable}
                data={data}
                columns={columns}
                keyField={ITEM_KEY_ID}
                pagination={{
                  options: {
                    sizePerPage: pagination.sizePerPage,
                    page: pagination.page,
                    totalSize,
                  },
                }}
                remote={{
                  pagination: true,
                  sort: true,
                }}
                onTableChange={handleChangeTable}
                selectRow={dataTableSelectRowProps}
                defaultSorted={[
                  {
                    dataField: 'maxDeliveryDateTime',
                    order: 'desc',
                  },
                ]}
                noDataIndicationComponent={getNoRecordsIndicator}
                hideableStorageKey="Dashboard-Carrier-In-Warehouse-Data-Table"
                toggleHideableColumns
                excludeHideableFieldColumns={['title', 'itemStateTypeId']}
              />
            </DashboardVariantV2CarrierContentDataTableContainer>
          </Stack>
        </Collapse>

        {openDialogUpdateGeoError && (
          <GeoError.UpdateDialog
            type="item"
            open={openDialogUpdateGeoError}
            onClose={() => toogleOpenDialogUpdateGeoError(false)}
            onSuccessSubmit={reloadDataAndResetSelectedRows}
          />
        )}

        <DialogConfimation
          open={openConfirmDeleteOrder}
          description={messageDialogConfimationDeleteOrder}
          onConfirm={handleDeleteOrder}
          onCancel={handleCloseConfirmDeleteOrder}
          loadingConfirm={loadingDeleteOrder}
        />

        {selectedCancelOrder && (
          <DialogCancelPartnerItem
            open={openDialogCancelOrder}
            onClose={handleCloseDialogCancelOrder}
            partnerCompany={selectedCancelOrder}
            onCancelSuccess={reloadDataAndResetSelectedRows}
          />
        )}

        {openDialogDetailsOrder && selectedDataForDetailsOrder && (
          <DialogDetailsOrder
            open={openDialogDetailsOrder}
            onClose={handleCloseDialogDetailsOrder}
            enabledChipTitle
            {...selectedDataForDetailsOrder}
          />
        )}

        {openConfirmChangeStateOrder && selectedOrderDataToChangeState && (
          <DialogChangeOrderState
            open={openConfirmChangeStateOrder}
            onClose={handleCloseDialogChangeStateOrder}
            {...selectedOrderDataToChangeState}
            onChangeSuccess={handleSuccessChangeOrdersState}
          />
        )}

        {openDialogModifyAssignmentOrder &&
          selectedItemIdToModifyAssignment && (
            <DialogModifyAssignmentOrders
              open={openDialogModifyAssignmentOrder}
              onClose={() => handleCloseDialogModifyAssignmentOrder()}
              itemIds={[selectedItemIdToModifyAssignment]}
              onSuccessModify={() => reloadDataAndResetSelectedRows()}
            />
          )}
      </>
    );
  };

export default DashboardVariantV2CarrierContentInWarehouseTabDataTable;
